import type { CurriculumObjectives, EvaluationExam } from '@interactive-room';
import { cleanLinkedSessionTitle } from '../types/linkedSessionContext';
import { curriculumReferenceItems } from './curriculumPrintUtils';

export interface GuideCurriculumGridItem {
  id: string;
  label: string;
  value: string;
}

export interface GuideHeaderPresentation {
  /** Título principal — identidad de la sesión. */
  primaryTitle: string;
  /** Badge secundario curricular (OA · asignatura · nivel). */
  curriculumBadge: string | null;
  /** true cuando el título proviene de una sesión vinculada. */
  isSessionLinked: boolean;
  /** Ítems para la grilla compacta MINEDUC. */
  curriculumGrid: GuideCurriculumGridItem[];
}

function buildCurriculumBadge(
  objectives: CurriculumObjectives,
  subjectName?: string,
): string | null {
  const parts: string[] = [];
  if (objectives.oaCode?.trim()) parts.push(objectives.oaCode.trim());
  if (subjectName?.trim()) parts.push(subjectName.trim());
  if (objectives.levelLabel?.trim()) parts.push(objectives.levelLabel.trim());
  return parts.length > 0 ? parts.join(' · ') : null;
}

function buildCurriculumGrid(
  objectives: CurriculumObjectives,
): GuideCurriculumGridItem[] {
  const items: GuideCurriculumGridItem[] = [];

  if (objectives.ejeLabel?.trim()) {
    items.push({ id: 'eje', label: 'Eje', value: objectives.ejeLabel.trim() });
  }

  const refs = curriculumReferenceItems(objectives);
  for (const ref of refs) {
    const key = ref.code.replace(/\s+/g, '_').toLowerCase();
    items.push({ id: key, label: ref.code, value: ref.text });
  }

  return items;
}

/**
 * Resuelve jerarquía semántica del encabezado — §6 ARCHETYPE_COMPOSER.
 */
export function resolveGuideHeaderPresentation(
  exam: Pick<EvaluationExam, 'title' | 'linkedSession' | 'curriculumObjectives'>,
  subjectName?: string,
): GuideHeaderPresentation {
  const isSessionLinked = Boolean(exam.linkedSession);
  const cleanedTitle = cleanLinkedSessionTitle(exam.title || '').trim();
  const primaryTitle =
    isSessionLinked && cleanedTitle
      ? cleanedTitle
      : cleanedTitle || 'Guía de aprendizaje';

  const objectives = exam.curriculumObjectives;
  const curriculumBadge = objectives ? buildCurriculumBadge(objectives, subjectName) : null;
  const curriculumGrid = objectives ? buildCurriculumGrid(objectives) : [];

  return {
    primaryTitle,
    curriculumBadge,
    isSessionLinked,
    curriculumGrid,
  };
}
